import React, { useState, useEffect, useRef } from "react";
import { Text, Icon, MixedString } from "@website-builder/ui/shared/elements";
import clsx from "clsx";
import {
	MainWrapper,
	BooleanItemWrapper,
	ErrorSection,
	SelectWrapper,
	SelectedItemWrapper,
	OptionsList,
	DownwardsArrow,
} from "./styles";
import { FIELD_TYPES } from "../HubspotForm/constants";
const Boolean = (props) => {
	const {
		type,
		options,
		value,
		name,
		className,
		label,
		helpText,
		required,
		showErrors,
		defaultValue = [],
		textColor,
		error = [{}, () => {}],
	} = props;
	const [errors, setErrors] = error;
	const [focused, setFocused] = useState(false);
	const [checkedState, setCheckedState] = useState([]);
	const [selectedItemsCount, setSelectedItemsCount] = useState(0);
	const [expanded, setExpanded] = useState(false);
	let activeItemRef = useRef(null);
	let selectWrapperRef = useRef(null);
	const validate = (value) => {
		if (required && !value?.some?.((item) => item)) {
			setErrors({
				...errors,
				[name]: "This is a required field.",
			});
			return;
		}
		setErrors({
			...errors,
			[name]: "",
		});
	};

	useEffect(() => {
		let initValues;
		if (type === FIELD_TYPES.SINGLE_CHECKBOX) {
			initValues = new Array(1);
			initValues[0] =
				defaultValue?.[0] === "true" || defaultValue?.[0] === true;
		} else {
			initValues = options
				?.filter(({ hidden }) => !hidden)
				?.map(({ value }) => defaultValue.some((item) => item === value));
		}
		setCheckedState(initValues);
	}, []);

	useEffect(() => {
		const setExpandOnClickEvent = (e) => {
			if (!selectWrapperRef?.current?.contains(e.target)) {
				setExpanded(false);
			}
		};
		window.addEventListener("click", setExpandOnClickEvent);
		return () => {
			window.removeEventListener("click", setExpandOnClickEvent);
		};
	}, [activeItemRef, selectWrapperRef]);

	useEffect(() => {
		const setExpandOnEscapeEvent = (e) => {
			if (e.key === "Escape") {
				setExpanded(false);
			}
		};
		window.addEventListener("keydown", setExpandOnEscapeEvent);
		return () => {
			window.removeEventListener("click", setExpandOnEscapeEvent);
		};
	}, []);

	const handleOnFocus = () => setFocused(true);
	const handleOnBlur = () => {
		validate(checkedState);
		setFocused(false);
	};
	const handleOnChange = (position) => {
		const updatedCheckedState = checkedState.map((item, index) => {
			const value = type === "radio" ? false : item;
			return index === position ? !item : value;
		});
		setCheckedState(updatedCheckedState);
		validate(updatedCheckedState);
		setSelectedItemsCount(updatedCheckedState.filter((item) => item).length);
	};

	const singleCheckboxJSX = (
		<BooleanItemWrapper>
			<label
				className={`${
					required ? "required" : ""
				} boolean-input-text label-text`}
			>
				<input
					type="checkbox"
					id={label}
					name={name}
					value={checkedState?.[0]}
					data-is-single-checkbox={true}
					onFocus={handleOnFocus}
					onBlur={handleOnBlur}
					onChange={(e) => handleOnChange(0)}
					checked={checkedState?.[0]}
				/>
				<MixedString mixedString={label} color={textColor} />
			</label>
		</BooleanItemWrapper>
	);

	const multiCheckboxJSX = (
		<SelectWrapper ref={selectWrapperRef}>
			<SelectedItemWrapper ref={activeItemRef}>
				<button
					className="btn placeholder"
					onClick={(e) => {
						e.preventDefault();
						setExpanded(true);
					}}
					disabled={expanded}
				>
					{selectedItemsCount} items selected
				</button>
			</SelectedItemWrapper>
			<OptionsList expanded={expanded}>
				{options
					?.filter(({ hidden }) => !hidden)
					?.map(({ label, value }, index) => (
						<BooleanItemWrapper key={value}>
							<label>
								<input
									type={type}
									id={name}
									name={name}
									value={value}
									onFocus={handleOnFocus}
									onBlur={handleOnBlur}
									onChange={() => handleOnChange(index)}
									checked={checkedState?.[index]}
								/>
								<Text color={textColor}>{label}</Text>
							</label>
						</BooleanItemWrapper>
					))}
			</OptionsList>
			<DownwardsArrow />
		</SelectWrapper>
	);

	// Typically used with Radio Buttons
	const defaultBooleanJSX = options
		?.filter(({ hidden }) => !hidden)
		?.map(({ label, value }, index) => (
			<BooleanItemWrapper key={value}>
				<label>
					<input
						type={type}
						id={name}
						name={name}
						value={value}
						onFocus={handleOnFocus}
						onBlur={handleOnBlur}
						onChange={() => handleOnChange(index)}
						checked={checkedState?.[index]}
					/>
					<Text color={textColor}>{label}</Text>
				</label>
			</BooleanItemWrapper>
		));

	return (
		<MainWrapper className={className}>
			{label && type !== FIELD_TYPES.SINGLE_CHECKBOX ? (
				<Text
					variant="label_M"
					className={`${
						required ? "required" : ""
					} label-text boolean-input-text`}
				>
					{label}
				</Text>
			) : null}
			{helpText ? (
				<Text
					variant="paragraph_S_bold"
					className="help-text"
					color={textColor}
				>
					{helpText}
				</Text>
			) : null}
			{(() => {
				switch (type) {
					case FIELD_TYPES.SINGLE_CHECKBOX:
						return singleCheckboxJSX;
					case FIELD_TYPES.MULTI_CHECKBOX:
						return multiCheckboxJSX;
					default:
						return defaultBooleanJSX;
				}
			})()}
			{errors[name] && !focused ? (
				<ErrorSection className="error-section">
					<Icon iconName="warning" />
					<Text variant="paragraph_S" className="errorMessage">
						{errors[name]}
					</Text>
				</ErrorSection>
			) : null}
		</MainWrapper>
	);
};

export default Boolean;
